import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getEventSummary } from '../Utils/timeEvent';
import CloseButton from './Common/CloseButton';

const looksLikeHtml = (text) => {
  if (!text || typeof text !== 'string') return false;
  return /<\/?[a-z][\s\S]*>/i.test(text);
};

const renderStructuredBlock = (title, content) => {
  if (!content) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {title}
      </Typography>
      <Box
        component="pre"
        sx={{
          backgroundColor: '#f5f5f5',
          borderRadius: 1,
          p: 1.5,
          maxHeight: 260,
          overflowY: 'auto',
          fontSize: '0.8rem'
        }}
      >
        {JSON.stringify(content, null, 2)}
      </Box>
    </Box>
  );
};

const tryParseJson = (value) => {
  if (typeof value !== 'string') return null;
  try { return JSON.parse(value); } catch { return null; }
};

const buildIsolatedHtml = (innerHtml) => (
  `<!DOCTYPE html>\n<html>\n  <head>\n    <meta charset="utf-8" />\n    <style>` +
  `*{box-sizing:border-box;} html,body{margin:0;padding:0;width:100%;height:100%;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:12px;line-height:1.4;color:#333;} ` +
  `img,video,canvas,svg,iframe{max-width:100%;height:auto;} table{width:100%;border-collapse:collapse;} pre,code{white-space:pre-wrap;word-break:break-word;}` +
  `</style>\n  </head>\n  <body>` + (innerHtml || '') + `</body>\n</html>`
);

const renderResponsePreview = (title, responseData) => {
  if (!responseData) return null;

  let obj = null;
  if (typeof responseData === 'string') obj = tryParseJson(responseData);
  else if (typeof responseData === 'object') obj = responseData;

  const answerContent = obj && typeof obj === 'object'
    ? (obj.answer || '')
    : (typeof responseData === 'string' ? responseData : '');
  const answerFormat = obj && typeof obj === 'object'
    ? (obj.answerFormat || 'plaintext')
    : 'plaintext';

  if (!answerContent) return null;

  const lowerFmt = (answerFormat || '').toLowerCase();
  const isHtml = lowerFmt.includes('html') || lowerFmt.includes('javascript') || looksLikeHtml(answerContent);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {title}
      </Typography>
      {isHtml ? (
        <Box sx={{ border: '1px solid rgba(87, 87, 176, 0.2)', borderRadius: 1, overflow: 'hidden' }}>
          <iframe
            title="widget-answer-preview"
            srcDoc={buildIsolatedHtml(answerContent)}
            style={{ width: '100%', height: '320px', border: '0' }}
          />
        </Box>
      ) : (
        <Box
          component="pre"
          sx={{
            backgroundColor: 'rgba(87, 87, 176, 0.02)',
            border: '1px solid rgba(87, 87, 176, 0.2)',
            borderRadius: 1,
            p: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          {answerContent}
        </Box>
      )}
    </Box>
  );
};

const formatTagReply = (value) => {
  if (value === undefined || value === null || value === '') {
    return '—';
  }
  const parsed = tryParseJson(value);
  if (parsed && typeof parsed === 'object') {
    return JSON.stringify(parsed, null, 2);
  }
  return String(value);
};

const renderTagList = (tags, t) => {
  if (!Array.isArray(tags) || tags.length === 0) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {t('widgetSnapshot.tagsSection', 'Tags')}
      </Typography>
      <Box
        component="ul"
        sx={{
          listStyle: 'disc',
          pl: 3,
          mb: 0
        }}
      >
        {tags.map((tag) => (
          <li key={`${tag.id || tag.variable || Math.random()}`}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {tag.variable || t('widgetSnapshot.untitled', 'Untitled')}{' '}
              <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                ({tag.description || t('widgetSnapshot.noDescription', 'No description')})
              </Typography>
            </Typography>
            <Typography
              variant="body2"
              sx={{
                backgroundColor: '#f7f7fb',
                border: '1px solid rgba(87, 87, 176, 0.15)',
                borderRadius: 1,
                p: 1,
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {formatTagReply(tag.reply ?? tag.value ?? '')}
            </Typography>
          </li>
        ))}
      </Box>
    </Box>
  );
};

const renderTimeEventSections = (snapshotData, t) => {
  const summary = getEventSummary({
    customFields: snapshotData?.tags || snapshotData?.timeevent?.customFields || []
  });

  return (
    <>
      {renderStructuredBlock(t('widgetSnapshot.eventMetadata', 'Event Metadata'), snapshotData?.timeevent)}

      {summary && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('widgetSnapshot.summary', 'Summary')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
            {summary}
          </Typography>
        </Box>
      )}

      {snapshotData?.timeevent?.eventnote && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('widgetSnapshot.note', 'Note')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
            {snapshotData.timeevent.eventnote}
          </Typography>
        </Box>
      )}

      {renderTagList(snapshotData?.tags, t)}

      {snapshotData?.event_payload && renderStructuredBlock(t('widgetSnapshot.eventData', 'Event Data'), snapshotData.event_payload)}
    </>
  );
};

const WidgetSnapshotDialog = ({ open, onClose, snapshotContext, formatTimestamp }) => {
  const { t } = useTranslation();
  const snapshotData = snapshotContext?.data;
  const snapshotType = snapshotContext?.type
    || (snapshotData?.widget ? 'widget' : snapshotData?.timeevent ? 'timeevent' : 'widget');
  const dialogTitle = snapshotType === 'timeevent'
    ? t('widgetSnapshot.timeEventTitle', 'Time Event Snapshot')
    : t('widgetSnapshot.widgetTitle', 'Widget Snapshot');
  const capturedLabel = t('widgetSnapshot.capturedAt', 'Captured');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {dialogTitle}
          </Typography>
          {snapshotContext?.log?.action_name && (
            <Typography variant="caption" color="text.secondary">
              {snapshotContext.log.action_name}
            </Typography>
          )}
          {snapshotData?.snapshot_taken_at && (
            <Typography variant="caption" color="text.secondary">
              {capturedLabel} {formatTimestamp(snapshotData.snapshot_taken_at)}
            </Typography>
          )}
        </Box>
        <CloseButton size="small" onClick={onClose} />
      </DialogTitle>
      <DialogContent dividers>
        {snapshotType === 'widget' && (
          <>
            {snapshotData?.widget && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t('widgetSnapshot.widgetSection', 'Widget')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {(snapshotData.widget.title || t('widgetSnapshot.untitled', 'Untitled')) +
                    ' (' +
                    (snapshotData.widget.variable || t('widgetSnapshot.noVariable', 'No variable')) +
                    ')'}
                </Typography>
              </Box>
            )}
            {snapshotData?.widget?.description && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t('widgetSnapshot.description', 'Description')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {snapshotData.widget.description}
                </Typography>
              </Box>
            )}
            {renderStructuredBlock(t('widgetSnapshot.contextMetadata', 'Context Metadata'), snapshotData?.context)}
            {renderResponsePreview(t('widgetSnapshot.widgetSection', 'Widget'), snapshotData?.new_response)}
            {(() => {
              const nr = typeof snapshotData?.new_response === 'string'
                ? tryParseJson(snapshotData.new_response)
                : snapshotData?.new_response;
              const reason = nr && typeof nr === 'object' ? nr.reason : null;
              return reason ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t('widgetSnapshot.reasoning', 'Reasoning')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {reason}
                  </Typography>
                </Box>
              ) : null;
            })()}
            
            {renderResponsePreview(t('widgetSnapshot.previousResponse', 'Previous Response'), snapshotData?.previous_response)}
            
            {renderStructuredBlock(t('widgetSnapshot.previousResponseMeta', 'Previous Response Metadata'), snapshotData?.previous_response_meta)}
          </>
        )}

        {snapshotType === 'timeevent' && renderTimeEventSections(snapshotData, t)}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t('common.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WidgetSnapshotDialog;
